import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["overlay", "dialog", "player", "title"];
  static values = { open: Boolean };

  connect() {
    this._ytApiReady = false;
    this._ytPlayer = null;
    this._loadYouTubeApi();
  }

  open(event) {
    event.preventDefault();
    const url = event.currentTarget.dataset.videoUrl;
    const title = event.currentTarget.dataset.videoTitle;
    if (!url) return;

    const youtubeId = this.parseYouTubeId(url);

    // Use YT Player API for YouTube videos when available
    if (youtubeId && this._ytApiReady) {
      this.titleTarget.textContent = title || "";
      this.playerTarget.innerHTML = "";
      this._removeEndOverlay();

      // Create sandboxed iframe ourselves, then attach YT API to it
      const iframe = document.createElement("iframe");
      iframe.id = "yt-player-" + Date.now();
      iframe.src = `https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`;
      iframe.setAttribute("allow", "autoplay; encrypted-media");
      iframe.setAttribute("allowfullscreen", "");
      iframe.setAttribute("frameborder", "0");
      iframe.setAttribute(
        "sandbox",
        "allow-scripts allow-same-origin allow-presentation"
      );
      iframe.className = "kids-video-modal__iframe";
      iframe.title = "Video player";
      this.playerTarget.appendChild(iframe);

      // Attach YT Player API to the existing sandboxed iframe
      this._ytPlayer = new window.YT.Player(iframe.id, {
        events: {
          onStateChange: this._onYtStateChange.bind(this),
        },
      });

      this._previousFocus = document.activeElement;
      this.openValue = true;
      return;
    }

    // Fallback for non-YouTube or if API hasn't loaded
    const embed = this.buildEmbed(url);
    if (!embed) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    this.titleTarget.textContent = title || "";
    this.playerTarget.innerHTML = "";
    this._removeEndOverlay();
    this.playerTarget.appendChild(embed);
    this._previousFocus = document.activeElement;
    this.openValue = true;
  }

  close() {
    this.openValue = false;
    if (this._ytPlayer) {
      this._ytPlayer.destroy();
      this._ytPlayer = null;
    }
    this.playerTarget.innerHTML = "";
    this._removeEndOverlay();
  }

  replay() {
    this._removeEndOverlay();
    if (this._ytPlayer && typeof this._ytPlayer.seekTo === "function") {
      this._ytPlayer.seekTo(0);
      this._ytPlayer.playVideo();
    }
  }

  closeOnBackground(event) {
    if (event.target === this.overlayTarget) {
      this.close();
    }
  }

  closeOnEscape(event) {
    if (event.key === "Escape") {
      this.close();
    }
  }

  openValueChanged() {
    if (this.openValue) {
      this.overlayTarget.classList.add("kids-video-modal--open");
      document.body.style.overflow = "hidden";
      this.dialogTarget.focus();
    } else {
      this.overlayTarget.classList.remove("kids-video-modal--open");
      document.body.style.overflow = "";
      if (this._previousFocus) {
        this._previousFocus.focus();
        this._previousFocus = null;
      }
    }
  }

  // --- YouTube IFrame Player API ---

  _loadYouTubeApi() {
    if (window.YT && window.YT.Player) {
      this._ytApiReady = true;
      return;
    }

    // Only load the script once globally
    if (document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      this._waitForYtApi();
      return;
    }

    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);
    this._waitForYtApi();
  }

  _waitForYtApi() {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      this._ytApiReady = true;
      if (typeof prev === "function") prev();
    };
  }

  _onYtStateChange(event) {
    // YT.PlayerState.ENDED === 0
    if (event.data === 0) {
      this._showEndOverlay();
    }
  }

  _showEndOverlay() {
    if (this.playerTarget.querySelector(".kids-video-modal__end-overlay")) return;

    const overlay = document.createElement("div");
    overlay.className = "kids-video-modal__end-overlay";
    overlay.innerHTML = `
      <div class="kids-video-modal__end-content">
        <p class="kids-video-modal__end-text">Great video!</p>
        <button class="kids-video-modal__end-btn kids-video-modal__end-btn--replay"
                data-action="video-modal#replay"
                type="button">
          Watch again
        </button>
        <button class="kids-video-modal__end-btn kids-video-modal__end-btn--close"
                data-action="video-modal#close"
                type="button">
          Pick another video
        </button>
      </div>
    `;
    this.playerTarget.appendChild(overlay);
  }

  _removeEndOverlay() {
    const overlay = this.playerTarget.querySelector(
      ".kids-video-modal__end-overlay"
    );
    if (overlay) overlay.remove();
  }

  // --- Fallback embed builders (Vimeo, MP4) ---

  buildEmbed(url) {
    const youtubeId = this.parseYouTubeId(url);
    if (youtubeId) return this.youtubeIframeFallback(youtubeId);

    const vimeoId = this.parseVimeoId(url);
    if (vimeoId) return this.vimeoIframe(vimeoId);

    if (this.isMp4(url)) return this.mp4Video(url);

    return null;
  }

  parseYouTubeId(url) {
    const patterns = [
      /youtu\.be\/([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  }

  parseVimeoId(url) {
    const patterns = [
      /vimeo\.com\/(\d+)/,
      /player\.vimeo\.com\/video\/(\d+)/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  }

  isMp4(url) {
    try {
      const pathname = new URL(url).pathname;
      return pathname.toLowerCase().endsWith(".mp4");
    } catch {
      return false;
    }
  }

  // Sandboxed fallback if YT API didn't load
  youtubeIframeFallback(videoId) {
    const iframe = document.createElement("iframe");
    iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;
    iframe.setAttribute("allow", "autoplay; encrypted-media");
    iframe.setAttribute("allowfullscreen", "");
    iframe.setAttribute("frameborder", "0");
    iframe.setAttribute(
      "sandbox",
      "allow-scripts allow-same-origin allow-presentation"
    );
    iframe.className = "kids-video-modal__iframe";
    iframe.title = "Video player";
    return iframe;
  }

  vimeoIframe(videoId) {
    const iframe = document.createElement("iframe");
    iframe.src = `https://player.vimeo.com/video/${videoId}?autoplay=1`;
    iframe.setAttribute("allow", "autoplay; fullscreen");
    iframe.setAttribute("allowfullscreen", "");
    iframe.setAttribute("frameborder", "0");
    iframe.className = "kids-video-modal__iframe";
    iframe.title = "Video player";
    return iframe;
  }

  mp4Video(url) {
    const video = document.createElement("video");
    video.src = url;
    video.controls = true;
    video.autoplay = true;
    video.className = "kids-video-modal__video";
    video.setAttribute("controlslist", "nodownload");
    video.textContent = "Your browser does not support the video tag.";
    return video;
  }
}
